import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageSeo } from "@/components/PageSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { API_BASE_URL } from "@/lib/api";

const Contact = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", honey: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // Simple math captcha
  const captcha = useMemo(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    return { a, b, answer: a + b };
  }, []);
  const [captchaInput, setCaptchaInput] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (parseInt(captchaInput) !== captcha.answer) {
      setError(t("contact.captchaError"));
      return;
    }

    setSending(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          subject: form.subject,
          message: form.message,
          honey: form.honey,
          captcha_answer: parseInt(captchaInput),
          captcha_expected: captcha.answer,
        }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || "Failed to send");
      }
      setSent(true);
    } catch (err: any) {
      setError(err.message || t("contact.sendError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageSeo
        title={t("contact.seoTitle")}
        description={t("contact.seoDescription")}
      />
      <Header />

      <main className="flex-1">
        <section className="py-12 sm:py-16 lg:py-20">
          <div className="container mx-auto px-4 max-w-2xl">
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-4">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
                {t("contact.title")}
              </h1>
              <p className="text-muted-foreground text-lg">
                {t("contact.description")}
              </p>
            </div>

            {sent ? (
              <Card>
                <CardContent className="flex flex-col items-center py-12 text-center">
                  <CheckCircle2 className="h-16 w-16 text-green-500 mb-4" />
                  <h2 className="text-2xl font-bold mb-2">{t("contact.thankYouTitle")}</h2>
                  <p className="text-muted-foreground">{t("contact.thankYouMessage")}</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Honeypot - hidden from users */}
                    <input
                      type="text"
                      name="honey"
                      value={form.honey}
                      onChange={(e) => setForm({ ...form, honey: e.target.value })}
                      style={{ position: "absolute", left: "-9999px", opacity: 0 }}
                      tabIndex={-1}
                      autoComplete="off"
                    />

                    <div className="space-y-2">
                      <Label htmlFor="name">{t("contact.nameLabel")}</Label>
                      <Input
                        id="name"
                        required
                        maxLength={200}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">{t("contact.emailLabel")}</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject">{t("contact.subjectLabel")}</Label>
                      <Input
                        id="subject"
                        required
                        maxLength={300}
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">{t("contact.messageLabel")}</Label>
                      <Textarea
                        id="message"
                        required
                        rows={5}
                        minLength={10}
                        maxLength={5000}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="captcha">
                        {t("contact.captchaLabel", { a: captcha.a, b: captcha.b })}
                      </Label>
                      <Input
                        id="captcha"
                        type="number"
                        required
                        value={captchaInput}
                        onChange={(e) => setCaptchaInput(e.target.value)}
                        className="max-w-[120px]"
                      />
                    </div>

                    {error && (
                      <p className="text-sm text-destructive">{error}</p>
                    )}

                    <Button type="submit" disabled={sending} className="w-full">
                      <Send className="h-4 w-4 mr-2" />
                      {sending ? t("contact.sending") : t("contact.send")}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
